import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Nav, NavItem, NavLink } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import CustomFieldsSettings from './CustomFieldsSettings';

/**
 * Sub-tab container for custom fields, mirroring CategoriesManagement.
 * "Custom Fields" (top tab) → entity sub-tabs ("Asset Custom Fields" now;
 * Work Orders / Parts land here later, each as its own sub-tab).
 */
const CustomFieldsManagement = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const visibleTabs = useMemo(() => {
    const config = [
      {
        key: 'asset-custom-fields',
        label: t('custom_fields.asset_custom_fields', 'Asset Custom Fields'),
        icon: 'box',
        path: '/settings/asset-custom-fields',
        visible: true,
        component: CustomFieldsSettings,
      },
      // Future: Work Order / Parts custom fields, each gated on its own entity.
    ];
    return config.filter((tab) => tab.visible);
  }, [t]);

  const activeTab = visibleTabs.find((tab) => tab.path === location.pathname) || visibleTabs[0];

  // Landing on the bare /settings/custom-fields path redirects to the first sub-tab.
  useEffect(() => {
    if (location.pathname === '/settings/custom-fields' && visibleTabs.length > 0) {
      navigate(visibleTabs[0].path, { replace: true });
    }
  }, [location.pathname, visibleTabs, navigate]);

  useEffect(() => {
    document.title = `${t('shared.custom_fields', 'Custom Fields')} | Arcmedix`;
  }, [location.pathname, t]);

  if (!activeTab) return null;

  const ActiveComponent = activeTab.component;

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex align-items-center mb-4 border-bottom">
          <Nav tabs className="custom-fields-management-tabs flex-grow-1">
            {visibleTabs.map((tab) => (
              <NavItem key={tab.key}>
                <NavLink
                  className={classnames({ active: activeTab.key === tab.key })}
                  onClick={() => navigate(tab.path)}
                  style={{ cursor: 'pointer' }}
                >
                  <FeatherIcon icon={tab.icon} size={16} className="me-2" />
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>

        <ActiveComponent />
      </div>

      <style>{`
        .custom-fields-management-tabs {
          gap: 0.5rem;
        }
        .custom-fields-management-tabs .nav-link {
          color: #6c757d;
          background-color: #fff;
        }
        .custom-fields-management-tabs .nav-link.active {
          color: #3980c0;
          background-color: #fff;
          border-bottom: 2px solid #3980c0;
        }
      `}</style>
    </div>
  );
};

export default CustomFieldsManagement;
